import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../../api/index";
import { jwtDecode } from "jwt-decode";
import { IoMdArrowBack } from "react-icons/io";
import { FiArrowUpRight, FiArrowDownLeft, FiHome ,FiUser ,FiPlusSquare, FiMail, FiCreditCard, FiClock, FiPlus, FiPieChart } from "react-icons/fi";

const BalancePage = () => {
  const token = localStorage.getItem("token") || "";
  const decoded = jwtDecode(token);

  const typeofuser = decoded.typeofuser;
  const navigate = useNavigate();
  const { id } = useParams();

  const [bonus, setBonus] = useState(0);
  const [amount, setAmount] = useState(0);
  const [total, setTotal] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const balanceRes = await api.getUserBalance(id);
        const fetchedBonus = Number(balanceRes.data.bonus) || 0;
        const fetchedAmount = Number(balanceRes.data.amount) || 0;

        setBonus(fetchedBonus);
        setAmount(fetchedAmount);
        setTotal(fetchedAmount + fetchedBonus);

        const transactionsRes = await api.getUserPayments(id);
        setTransactions(transactionsRes.data.transactions || []);
      } catch (err) {
        // setError("Failed to load account info.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  const handleWithdraw = () => {
    if (amount <= 0) {
      setError("No available balance to withdraw");
      return;
    }
    navigate("/withdraw", { state: { amount } });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
        <p className="mt-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">Verifying Vault...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700">
          <IoMdArrowBack size={24} />
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Finance</h1>
        <div className="w-10"></div>
      </div>

      <div className="max-w-md mx-auto p-6">
        {/* 💳 Total Balance Card */}
        <div className="bg-gray-900 rounded-[2.5rem] p-8 mb-8 relative overflow-hidden shadow-2xl shadow-gray-400">
          <div className="absolute top-[-20%] right-[-10%] w-40 h-40 bg-orange-600/20 rounded-full blur-3xl"></div>
          
          <div className="relative z-10 text-center">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Total Net Worth</p>
            <h2 className="text-4xl font-black text-white mb-8">
               ₦{total.toLocaleString()}
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => navigate("/fundwallet")}
                className="flex flex-col items-center gap-2 bg-white/10 hover:bg-white/20 p-4 rounded-3xl transition"
              >
                <div className="w-10 h-10 bg-orange-600 rounded-2xl flex items-center justify-center text-white">
                  <FiPlus strokeWidth={3} />
                </div>
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Fund</span>
              </button>

              <button 
                onClick={handleWithdraw}
                className="flex flex-col items-center gap-2 bg-white/10 hover:bg-white/20 p-4 rounded-3xl transition"
              >
                <div className="w-10 h-10 bg-green-500 rounded-2xl flex items-center justify-center text-white">
                  <FiArrowUpRight strokeWidth={3} />
                </div>
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Withdraw</span>
              </button>
            </div>
          </div>
        </div>

        {/* 💰 Account Breakdown */}
        <div className="space-y-4 mb-8">
          <div className="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center">
                <FiArrowDownLeft size={20} strokeWidth={3}/>
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Withdrawable</p>
                <p className="text-lg font-black text-gray-900">₦{amount.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-green-100 px-3 py-1 rounded-full">
               <p className="text-[10px] font-black text-green-700 uppercase">Ready</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center">
                <FiPieChart size={20} strokeWidth={3}/>
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Bonus Balance</p>
                <p className="text-lg font-black text-gray-900">₦{bonus.toLocaleString()}</p>
              </div>
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase pr-2">Locked</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-2xl text-xs font-black text-center mb-6 border border-red-100 animate-bounce">
            {error}
          </div>
        )}

        {/* 🕒 Transaction History Section */}
        <div className="bg-white rounded-[2rem] border border-gray-100 overflow-hidden shadow-sm">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition"
          >
            <div className="flex items-center gap-3">
              <FiClock className="text-gray-400" />
              <span className="text-sm font-black text-gray-900 uppercase tracking-widest">Transaction History</span>
            </div>
            <span className={`text-orange-600 transition-transform duration-300 ${showHistory ? 'rotate-180' : ''}`}>
               <FiArrowDownLeft />
            </span>
          </button>

       {showHistory && (
  <div className="px-6 pb-6">
    {transactions.length === 0 ? (
      <div className="text-center py-10">
        <p className="text-[10px] font-black text-gray-300 uppercase">Your ledger is empty</p>
      </div>
    ) : (
      <ul className="space-y-3">
        {transactions.map((t) => (
          <li
            key={t._id}
            className="flex justify-between items-center bg-gray-50 p-4 rounded-2xl border border-gray-100"
          >
            <div className="flex items-center gap-3">
              {/* Dynamic status colors */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                t.status === "success" ? 'bg-green-100 text-green-600' : 
                t.status === "pending" ? 'bg-amber-100 text-amber-600' : 
                'bg-red-100 text-red-600'
              }`}>
                {/* Dynamic icons */}
                {t.status === "success" ? <FiArrowDownLeft size={14}/> : 
                 t.status === "pending" ? <FiClock size={14}/> : 
                 <FiPlus size={14}/>}
              </div>
              <div>
                <p className="text-xs font-black text-gray-900 uppercase tracking-tighter">
                  {/* Dynamic labels */}
                  {t.status === "success" ? "Credit" : 
                   t.status === "pending" ? "Pending" : 
                   "Failed"}
                </p>
                <p className="text-[9px] font-bold text-gray-400">{new Date(t.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-black text-gray-900">₦{t.amount.toLocaleString()}</p>
              <p className="text-[9px] font-black text-gray-400 uppercase">{t.currency || "NGN"}</p>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
)}
        </div>
      </div>
         <nav className="fixed bottom-0 left-0  hidden  right-0 bg-white/80 backdrop-blur-xl border-t border-gray-100 md:flex justify-around items-center py-4 z-50 shadow-[0_-10px_30px_rgba(0,0,0,0.03)]">
              <button onClick={() => navigate("/")} className="flex flex-col items-center gap-1  text-gray-400 transition-all">
                <FiHome size={22} />
                <span className="text-[9px] font-black uppercase tracking-widest">Home</span>
              </button>
      
              <button onClick={() => navigate("/sellproduct")} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all">
                <FiPlusSquare size={22} />
                <span className="text-[9px] font-black uppercase tracking-widest">Post</span>
              </button>
      
                 <button onClick={() => navigate(`/balancepage/${id}`)} className="flex flex-col items-center gap-1 text-orange-600  transition-all">
                <FiCreditCard size={22} />
                <span className="text-[9px] font-black uppercase tracking-widest">Wallet</span>
              </button>
      
              <button onClick={() => navigate("/conversations")} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all">
                <FiMail size={22} />
                <span className="text-[9px] font-black uppercase tracking-widest">Inbox</span>
              </button>
      
              <button 
                onClick={() => typeofuser === "Customer" ? navigate("/profile") : navigate("/profile-seller")} 
                className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all"
              >
                <FiUser size={22} />
                <span className="text-[9px] font-black uppercase tracking-widest">Profile</span>
              </button>
            </nav>
    </div>
  );
};

export default BalancePage;